import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Eye, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { ExcelPreview } from './ExcelPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Small popover that fetches and renders an employee's pay-rate timeline.
// Lazy-loads on first open so we don't hammer the server with N requests.
const RateChangePopover = ({ employeeId, currentRate }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    if (history || loading) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/employees/${employeeId}/pay-rate-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setHistory(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover onOpenChange={(open) => { if (open) load(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="ml-1 inline-block px-1 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold hover:bg-amber-200"
          title="Pay rate changed during this period — click to see the timeline"
          data-testid={`rate-change-trigger-${employeeId}`}
        >Δ</button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end">
        <div className="text-xs font-semibold mb-2 text-slate-700">Pay rate timeline</div>
        {loading && <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
        {error && <div className="text-xs text-red-600">Failed to load: {error}</div>}
        {history && (
          <div className="space-y-1.5">
            {history.history.length === 0 ? (
              <div className="text-xs text-slate-500">
                No history recorded yet. Current rate: <strong>${history.current_rate}/hr</strong>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs border-b border-slate-200 pb-1.5">
                  <span className="text-slate-500">Current rate</span>
                  <span className="font-semibold text-slate-900">${history.current_rate}/hr</span>
                </div>
                {history.history.map((h) => (
                  <div key={h.id} className="text-xs flex items-start gap-2 pt-1.5 border-b border-slate-100 last:border-b-0 pb-1.5">
                    <span className="font-semibold text-slate-900 whitespace-nowrap">${h.rate}/hr</span>
                    <span className="text-slate-500 flex-1">
                      {h.effective_from} → {h.effective_to || 'now'}
                      {h.changed_by_name && (
                        <span className="block text-[10px] text-slate-400 mt-0.5">
                          by {h.changed_by_name}
                          {h.reason ? ` — ${h.reason}` : ''}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const PayrollExportDialog = ({ 
  open, 
  onOpenChange, 
  exportForm, 
  setExportForm, 
  onExport 
}) => {
  // On-screen preview state — fetched on demand. We render the same numbers
  // the Excel will contain so accounts can verify before downloading.
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Which Project-Totals rows the user has expanded. Each click toggles
  // the project_name into/out of this Set.
  const [expandedProjects, setExpandedProjects] = useState(() => new Set());

  // Build a project_name → [employee rows] index once per preview load so
  // expanding a row is O(1) instead of re-filtering the whole array on each
  // render.
  const employeesByProject = useMemo(() => {
    const map = new Map();
    if (!previewData?.project_rows) return map;
    for (const r of previewData.project_rows) {
      if (!map.has(r.project_name)) map.set(r.project_name, []);
      map.get(r.project_name).push(r);
    }
    return map;
  }, [previewData]);

  const fetchPreview = async () => {
    if (!exportForm.start_date || !exportForm.end_date) {
      toast.error('Pick a start and end date first');
      return;
    }
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/export/payroll/preview?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPreviewData(data);
    } catch (e) {
      toast.error('Could not load preview', { description: e.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Reset preview whenever dates change so the user doesn't see stale numbers.
  const handleDateChange = (field, value) => {
    setExportForm({ ...exportForm, [field]: value });
    setPreviewData(null);
    setExpandedProjects(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setPreviewData(null); onOpenChange(v); }}>
      <DialogContent
        data-testid="payroll-export-dialog"
        className={previewData ? 'sm:max-w-[95vw] max-h-[90vh] overflow-y-auto' : ''}
      >
        <DialogHeader>
          <DialogTitle>Export Payroll Data</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="payroll_start">Start Date *</Label>
              <Input
                id="payroll_start"
                type="date"
                value={exportForm.start_date}
                onChange={(e) => handleDateChange('start_date', e.target.value)}
                required
                data-testid="payroll-start-date"
              />
            </div>
            <div>
              <Label htmlFor="payroll_end">End Date *</Label>
              <Input
                id="payroll_end"
                type="date"
                value={exportForm.end_date}
                onChange={(e) => handleDateChange('end_date', e.target.value)}
                required
                data-testid="payroll-end-date"
              />
            </div>
          </div>

          {/* On-screen preview (renders the same numbers the Excel will). */}
          {previewData && (
            <div className="border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-blue-50 border-b text-sm font-medium text-blue-900 flex items-center justify-between flex-wrap gap-2">
                <span>
                  Preview — {previewData.rows.length} employees ·{' '}
                  Total {previewData.total_hours.toFixed(2)} hrs ·{' '}
                  ${previewData.total_pay.toFixed(2)}
                </span>
                <span className="text-xs text-blue-700">Scroll horizontally to see all days</span>
              </div>
              <div className="overflow-x-auto max-h-[55vh]">
                <table className="w-full text-xs" data-testid="payroll-preview-table">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left whitespace-nowrap sticky left-0 bg-slate-100 z-10">Employee</th>
                      <th className="px-2 py-2 text-left whitespace-nowrap">Account</th>
                      {previewData.day_headers.map((h) => (
                        <th key={h} className="px-2 py-2 text-center whitespace-nowrap text-blue-900">{h}</th>
                      ))}
                      <th className="px-2 py-2 text-right whitespace-nowrap">Total Hrs</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Rate</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Total Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((r, idx) => (
                      <tr key={`${r.employee_name}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-2 py-1.5 sticky left-0 z-10 font-medium whitespace-nowrap" style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                          {r.employee_name}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">{r.account}</td>
                        {r.daily_hours.map((h, i) => (
                          <td key={i} className={`px-2 py-1.5 text-center ${h > 0 ? 'text-slate-900' : 'text-slate-300'}`}>
                            {h > 0 ? h.toFixed(2) : '–'}
                          </td>
                        ))}
                        <td className="px-2 py-1.5 text-right font-semibold">{r.total_hours.toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-right">
                          ${r.hourly_rate}
                          {r.rate_changed && (
                            <RateChangePopover employeeId={r.employee_id} currentRate={r.hourly_rate} />
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold">${r.total_pay.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-200 font-bold">
                    <tr>
                      <td className="px-2 py-2 sticky left-0 bg-slate-200 z-10">TOTAL</td>
                      <td></td>
                      {previewData.column_day_totals.map((v, i) => (
                        <td key={i} className="px-2 py-2 text-center">{v > 0 ? v.toFixed(2) : '–'}</td>
                      ))}
                      <td className="px-2 py-2 text-right">{previewData.total_hours.toFixed(2)}</td>
                      <td></td>
                      <td className="px-2 py-2 text-right">${previewData.total_pay.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Second table: Hours per project per employee */}
              {previewData.project_rows && previewData.project_rows.length > 0 && (
                <div className="border-t">
                  <div className="px-3 py-2 bg-emerald-50 border-b text-sm font-medium text-emerald-900">
                    Hours by project — {previewData.project_rows.length} rows ·{' '}
                    Total {previewData.project_total_hours.toFixed(2)} hrs ·{' '}
                    ${previewData.project_total_pay.toFixed(2)}
                  </div>
                  <div className="overflow-x-auto max-h-[40vh]">
                    <table className="w-full text-xs" data-testid="payroll-project-table">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Employee</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Account</th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Project</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Hours</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Rate</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Pay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.project_rows.map((pr, i) => {
                          const prev = previewData.project_rows[i - 1];
                          const next = previewData.project_rows[i + 1];
                          const isNewEmp = !prev || prev.employee_name !== pr.employee_name;
                          const isLastForEmp = !next || next.employee_name !== pr.employee_name;
                          // Sum the employee's rows when this is the last
                          // line for that employee — used to render a small
                          // subtotal row below for workers who split between
                          // multiple projects.
                          let empSubtotalHours = 0;
                          let empSubtotalPay = 0;
                          let empProjectCount = 0;
                          if (isLastForEmp) {
                            for (let j = i; j >= 0; j--) {
                              if (previewData.project_rows[j].employee_name === pr.employee_name) {
                                empSubtotalHours += previewData.project_rows[j].hours;
                                empSubtotalPay += previewData.project_rows[j].pay;
                                empProjectCount += 1;
                              } else break;
                            }
                          }
                          return (
                            <React.Fragment key={i}>
                              <tr className={isNewEmp ? 'border-t-2 border-slate-300' : ''}>
                                <td className="px-2 py-1.5 font-medium whitespace-nowrap">{isNewEmp ? pr.employee_name : ''}</td>
                                <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{isNewEmp ? pr.account : ''}</td>
                                <td className="px-2 py-1.5 whitespace-nowrap">{pr.project_name}</td>
                                <td className="px-2 py-1.5 text-right">{pr.hours.toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right">${pr.hourly_rate}</td>
                                <td className="px-2 py-1.5 text-right">${pr.pay.toFixed(2)}</td>
                              </tr>
                              {/* Per-employee subtotal — only shown when the
                                  employee split hours across 2+ projects so
                                  single-project rows don't get noise. */}
                              {isLastForEmp && empProjectCount > 1 && (
                                <tr className="bg-amber-50 text-amber-900 font-semibold">
                                  <td className="px-2 py-1.5" colSpan={3}>
                                    Subtotal — {pr.employee_name} ({empProjectCount} projects)
                                  </td>
                                  <td className="px-2 py-1.5 text-right">{empSubtotalHours.toFixed(2)}</td>
                                  <td></td>
                                  <td className="px-2 py-1.5 text-right">${empSubtotalPay.toFixed(2)}</td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-200 font-bold">
                        <tr>
                          <td className="px-2 py-2" colSpan={3}>TOTAL</td>
                          <td className="px-2 py-2 text-right">{previewData.project_total_hours.toFixed(2)}</td>
                          <td></td>
                          <td className="px-2 py-2 text-right">${previewData.project_total_pay.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Third table: Project totals across all employees */}
              {previewData.project_totals && previewData.project_totals.length > 0 && (
                <div className="border-t">
                  <div className="px-3 py-2 bg-purple-50 border-b text-sm font-medium text-purple-900 flex items-center justify-between flex-wrap gap-2">
                    <span>
                      Project totals — {previewData.project_totals.length} project{previewData.project_totals.length === 1 ? '' : 's'} ·{' '}
                      Total {previewData.project_total_hours.toFixed(2)} hrs ·{' '}
                      ${previewData.project_total_pay.toFixed(2)}
                    </span>
                    <span className="text-xs text-purple-700">Click a project to see who worked on it</span>
                  </div>
                  <div className="overflow-x-auto max-h-[40vh]">
                    <table className="w-full text-xs" data-testid="payroll-project-totals-table">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="px-2 py-2 text-left whitespace-nowrap" style={{ width: '24px' }}></th>
                          <th className="px-2 py-2 text-left whitespace-nowrap">Project</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Total Hours</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Total Pay</th>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Employees</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.project_totals.map((pt, i) => {
                          const isOpen = expandedProjects.has(pt.project_name);
                          const empRows = employeesByProject.get(pt.project_name) || [];
                          const toggle = () => {
                            setExpandedProjects((prev) => {
                              const next = new Set(prev);
                              if (next.has(pt.project_name)) next.delete(pt.project_name);
                              else next.add(pt.project_name);
                              return next;
                            });
                          };
                          return (
                            <React.Fragment key={i}>
                              <tr
                                className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-purple-50 cursor-pointer transition-colors`}
                                onClick={toggle}
                                data-testid={`project-totals-row-${i}`}
                              >
                                <td className="px-2 py-1.5 text-slate-400">
                                  {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                </td>
                                <td className="px-2 py-1.5 font-medium whitespace-nowrap">{pt.project_name}</td>
                                <td className="px-2 py-1.5 text-right">{pt.hours.toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right font-semibold">${pt.pay.toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right text-slate-600">{pt.employee_count}</td>
                              </tr>
                              {isOpen && empRows.map((er, ei) => (
                                <tr key={`${i}-emp-${ei}`} className="bg-purple-50/40">
                                  <td className="px-2 py-1"></td>
                                  <td className="px-2 py-1 text-slate-700 pl-6 whitespace-nowrap">
                                    └ {er.employee_name}
                                    <span className="text-slate-400 text-[10px] ml-2">({er.account})</span>
                                  </td>
                                  <td className="px-2 py-1 text-right text-slate-700">{er.hours.toFixed(2)}</td>
                                  <td className="px-2 py-1 text-right text-slate-700">${er.pay.toFixed(2)}</td>
                                  <td className="px-2 py-1 text-right text-[10px] text-slate-400">@${er.hourly_rate}/hr</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-200 font-bold">
                        <tr>
                          <td></td>
                          <td className="px-2 py-2">TOTAL</td>
                          <td className="px-2 py-2 text-right">{previewData.project_total_hours.toFixed(2)}</td>
                          <td className="px-2 py-2 text-right">${previewData.project_total_pay.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 flex-wrap">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={fetchPreview}
              disabled={!exportForm.start_date || !exportForm.end_date || previewLoading}
              data-testid="payroll-preview-btn"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              {previewData ? 'Refresh Preview' : 'Show Preview'}
            </Button>
            <Button
              onClick={onExport}
              disabled={!exportForm.start_date || !exportForm.end_date}
              data-testid="payroll-download-btn"
            >
              Download Excel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ExpenseExportDialog = ({
  open,
  onOpenChange,
  exportForm,
  setExportForm,
  projects,
  onExport
}) => {
  const datesValid = !!(exportForm.start_date && exportForm.end_date);
  const projectQS = exportForm.project_id && exportForm.project_id !== 'all' ? `&project_id=${exportForm.project_id}` : '';
  const previewPath = datesValid
    ? `/api/export/project-expenses?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}${projectQS}`
    : null;
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="expense-export-dialog" className={datesValid ? 'sm:max-w-[90vw] max-h-[90vh] overflow-y-auto' : ''}>
      <DialogHeader>
        <DialogTitle>Export Project Expenses</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="expense_start">Start Date *</Label>
            <Input
              id="expense_start"
              type="date"
              value={exportForm.start_date}
              onChange={(e) => setExportForm({ ...exportForm, start_date: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="expense_end">End Date *</Label>
            <Input
              id="expense_end"
              type="date"
              value={exportForm.end_date}
              onChange={(e) => setExportForm({ ...exportForm, end_date: e.target.value })}
              required
            />
          </div>
        </div>
        <div>
          <Label htmlFor="expense_project">Project (Optional - leave blank for all)</Label>
          <Select
            value={exportForm.project_id}
            onValueChange={(value) => setExportForm({ ...exportForm, project_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(proj => (
                <SelectItem key={proj.id} value={proj.id}>{proj.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {previewPath && <ExcelPreview exportPath={previewPath} />}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onExport}
            disabled={!datesValid}
          >
            Download Excel
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export const MaterialOrdersExportDialog = ({
  open,
  onOpenChange,
  exportForm,
  setExportForm,
  onExport
}) => {
  const datesValid = !!(exportForm.start_date && exportForm.end_date);
  const previewPath = datesValid ? `/api/export/material-orders?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}` : null;
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="material-orders-export-dialog" className={datesValid ? 'sm:max-w-[90vw] max-h-[90vh] overflow-y-auto' : ''}>
      <DialogHeader>
        <DialogTitle>Export Material Orders</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="material_start">Start Date *</Label>
            <Input id="material_start" type="date" value={exportForm.start_date} onChange={(e) => setExportForm({ ...exportForm, start_date: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="material_end">End Date *</Label>
            <Input id="material_end" type="date" value={exportForm.end_date} onChange={(e) => setExportForm({ ...exportForm, end_date: e.target.value })} required />
          </div>
        </div>
        {previewPath && <ExcelPreview exportPath={previewPath} />}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onExport} disabled={!datesValid}>Download Excel</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export const CommunicationsExportDialog = ({
  open,
  onOpenChange,
  exportForm,
  setExportForm,
  onExport
}) => {
  const datesValid = !!(exportForm.start_date && exportForm.end_date);
  const previewPath = datesValid ? `/api/export/project-communications?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}` : null;
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="communications-export-dialog" className={datesValid ? 'sm:max-w-[90vw] max-h-[90vh] overflow-y-auto' : ''}>
      <DialogHeader>
        <DialogTitle>Export Project Communications</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="comms_start">Start Date *</Label>
            <Input id="comms_start" type="date" value={exportForm.start_date} onChange={(e) => setExportForm({ ...exportForm, start_date: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="comms_end">End Date *</Label>
            <Input id="comms_end" type="date" value={exportForm.end_date} onChange={(e) => setExportForm({ ...exportForm, end_date: e.target.value })} required />
          </div>
        </div>
        {previewPath && <ExcelPreview exportPath={previewPath} />}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onExport} disabled={!datesValid}>Download Excel</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export const StockExportDialog = ({
  open,
  onOpenChange,
  exportForm,
  setExportForm,
  onExport
}) => {
  const datesValid = !!(exportForm.start_date && exportForm.end_date);
  const previewPath = datesValid ? `/api/export/stock-movements?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}` : null;
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="stock-export-dialog" className={datesValid ? 'sm:max-w-[90vw] max-h-[90vh] overflow-y-auto' : ''}>
      <DialogHeader>
        <DialogTitle>Export Stock Movements</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="stock_start">Start Date *</Label>
            <Input id="stock_start" type="date" value={exportForm.start_date} onChange={(e) => setExportForm({ ...exportForm, start_date: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="stock_end">End Date *</Label>
            <Input id="stock_end" type="date" value={exportForm.end_date} onChange={(e) => setExportForm({ ...exportForm, end_date: e.target.value })} required />
          </div>
        </div>
        {previewPath && <ExcelPreview exportPath={previewPath} />}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onExport} disabled={!datesValid}>Download Excel</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export default {
  PayrollExportDialog,
  ExpenseExportDialog,
  MaterialOrdersExportDialog,
  CommunicationsExportDialog,
  StockExportDialog
};
